import { GCInstant } from 'src/lib/gcinstant';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import {
  stickerCampaign,
  stickerCampaignSchedule,
} from 'src/replicant/stickers/stickers.ruleset';
import StateObserver, { now, user } from 'src/StateObserver';
import { isPromoReady } from 'src/replicant/promo/promo.getters';
import { track } from 'src/lib/analytics/utils';
import type { EntryProperties } from 'src/lib/analytics/properties';

/** @returns Whether a creative ID or subfeature selects the sticker campaign. */
export function isStickersEntry(): boolean {
  // Entry data may not be available before platform initialization.
  const entry = GCInstant.entryData;
  if (!entry) return false;

  // Accept both platform attribution fields and unprefixed OA payload fields.
  const identifiers = [
    entry.$creativeAssetID,
    'creativeAssetID' in entry ? entry.creativeAssetID : undefined,
    entry.$subFeature,
    'subFeature' in entry ? entry.subFeature : undefined,
  ];

  // A lowercase singular marker also matches plural and mixed-case identifiers.
  return identifiers.some(
    (identifier) =>
      typeof identifier === 'string' &&
      identifier.toLowerCase().includes('sticker'),
  );
}

/**
 * Show an active sticker campaign and handle the Download CTA.
 * Display recording follows the existing promo readiness rules.
 * @param entry Source attribution, separate from the weekly launch gate.
 * @returns True if the popup was shown, or false if the campaign is unavailable.
 */
export async function openStickersPopup(
  entry: Pick<Partial<EntryProperties>, 'feature' | '$subFeature'>,
): Promise<boolean> {
  // Every entry must respect campaign dates and an explicitly disabled promo.
  const state = user();
  const timestamp = now();
  if (!stickerCampaignSchedule.hasActiveEvent(timestamp)) return false;
  if (state.promo.sponsoredStickers2026?.active === false) return false;

  // Record the display only when the existing promo API permits it.
  if (isPromoReady(state, 'sponsoredStickers2026', timestamp)) {
    await StateObserver.invoke.promoWasShown('sponsoredStickers2026');
  }

  // Keep the recorded count and source consistent across this display's events.
  const tracking = {
    originFeature: entry.feature,
    originSubFeature: entry.$subFeature,
    showCount: user().promo.sponsoredStickers2026?.count ?? 0,
  };

  // Record the opening and preserve attribution on the shared dialog events.
  track('StickerPopupOpen', tracking);
  const action = await openPopupPromise('popupStickers', {
    additionalTracking: {
      feature: entry.feature,
      $subFeature: entry.$subFeature,
      $creativeAssetID: GCInstant.entryData?.$creativeAssetID,
    },
  });
  // Closing with X records a dismissal without navigating away from the game.
  if (action !== 'download') {
    track('StickerPopupClose', tracking);
    return true;
  }

  // Record the Download choice before navigating away.
  track('StickerPopupDownload', tracking);

  // Match LINE sharing: Android opens a new tab; iOS hands off in-place.
  if (GCInstant.osType?.includes('ANDROID')) {
    window.open(stickerCampaign.downloadUrl);
  } else {
    window.location.assign(stickerCampaign.downloadUrl);
  }

  // The popup was shown and its download destination was opened.
  return true;
}
